import React, { FC, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { Box, Container, Grid, Typography } from '@material-ui/core';
import Navbar from 'components/Navbar';
import Footer from 'components/Footer/Index';
import useLocales from 'hooks/useLocales';
import { JobService } from 'services/jobs/job';
import Image from 'components/image/Image';
import Button from 'components/Button/Index';
import { getCareerLevelByValue, getSalaryByValue } from 'constants/constants';
import { DegreeEnum } from 'core/enum/degree';
import useAuth from 'hooks/useAuth';
import { companySizeChoices } from 'core/constants/aboutMePopUpConstants';
import { contractTypes } from 'core/constants/workType';
import { getJobSkillDisplayLabels } from 'utils/jobPostSkills';
import { CompanyService } from 'services/companes/companies';
import {
  JobCard,
  JobListDescription,
  JobOtherInfo,
  CompetencyTitle,
  CompetencyContainer,
  CompetencyTag,
  JobDetailHeader,
  JobDetailHeaderLogo,
  JobDetailHeaderContent,
  JobDetailHeaderLocation,
  JobDetailHeaderLocationIcon,
  JobDetailHeaderLocationText
} from '.';
import colors from 'assets/variables/colors';
import HtmlContent from 'components/HtmlContent';
import {
  getJobIndustryDisplayNames,
  getJobLocationDisplayNames
} from 'utils/jobIndustriesLocations';
import { SaveSkills } from '../../redux/slices/skills';
import { SavePublicCompanies } from '../../redux/slices/company';


const RootStyle = styled(Box)(() => ({
  position: 'relative',
  marginTop: '0',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh'
}));

const WrapperContent = styled(Box)(() => ({
  position: 'relative',
  flex: 1
}));

const GridStyle = styled(Grid)(() => ({
  '@media (max-width: 959px)': {
    flexWrap: 'wrap',
    rowGap: '20px'
  }
}));

const JobTitle = styled(Typography)(() => ({
  fontFamily: 'Rubik',
  fontWeight: 600,
  fontSize: '24px',
  color: '#131417',
  marginBottom: '8px',
  textAlign: 'left',
  lineHeight: 1.3
}));

const PublicJobDetail: FC = (): JSX.Element => {
  const dispatch = useDispatch();
  const { t, currentLang } = useLocales();
  const { slug } = useParams();
  const navigate = useNavigate();
  const { singleJob: job } = useSelector((state: RootState) => state.jobs);
  const { isAuthenticated, isCandidate, isCompanyUser } = useAuth();
  const { computerSkills: skills } = useSelector(
    (selector: RootState) => selector.computerSkills || []
  );

  const { publicCompanies } = useSelector((state: RootState) => state.company);

  const [jobSkills, setJobSkills] = useState<string[]>([]);
  const [disableActions, setDisableActions] = useState(false);
  const [isCompanyPublic, setIsCompanyPublic] = useState(false);

  useEffect(() => {
    if (slug) {
      getJobByIdOrSlug(slug, true);
    }
  }, [slug]);

  useEffect(() => {
    getPublicCompanies();
    return () => {
      dispatch(SaveSkills(null));
      dispatch(SavePublicCompanies(null));
    };
  }, []);

  const jobIndustries = useMemo(
    () => getJobIndustryDisplayNames(job?.industries, []),
    [job?.industries]
  );

  const jobLocations = useMemo(
    () => getJobLocationDisplayNames(job?.locations, []),
    [job?.locations]
  );

  useEffect(() => {
    if (job?.skills?.length) {
      mapJobSkills();
    } else {
      setJobSkills([]);
    }
  }, [job, skills, currentLang?.value]);

  useEffect(() => {
    // Check if job company exists in publicCompanies
    if (job?.company?.id && publicCompanies?.results?.length) {
      const companyExistsAndHasCareerPage = publicCompanies.results.some(
        (company: any) =>
          company.id === job.company.id && company?.has_career_page
      );
      setIsCompanyPublic(companyExistsAndHasCareerPage);
    }
  }, [job, publicCompanies]);

  const getJobByIdOrSlug = (param: number | string, isPublic: boolean = false): void => {
    JobService.getJobByIdOrSlug(param, isPublic);
  };

  const getPublicCompanies = (): void => {
    CompanyService.getPublicCompanies();
  };

  const mapJobSkills = (): void => {
    setJobSkills(
      getJobSkillDisplayLabels(
        job?.skills,
        currentLang?.value || 'en',
        skills || []
      )
    );
  };

  const handleApply = async (jobId: number) => {
    setDisableActions(true);
    try {
      const res = await JobService.applyForAJob(jobId);
      if (res) {
        // Show success notification
        console.log('Applied successfully');
        // Refresh job data to update the is_applied status
        getJobByIdOrSlug(jobId);
      } else {
        // Show error notification
        setDisableActions(false);
        console.log('Error applying for job');
      }
    } catch (error) {
      setDisableActions(false);
      console.error('Error applying for job:', error);
    }
  };

  const handleNavigateToLogin = () => {
    navigate('/login?role=candidate');
  };

  const handleCompanyClick = () => {
    if (isCompanyPublic && (job.company.slug || job?.company?.id)) {
      navigate(`/companies/${job.company.slug || job.company.id}`);
    }
  };

  // Find company size display name
  const getCompanySizeDisplay = (sizeValue: string) => {
    const size = companySizeChoices.find(
      (s) => String(s.value) === String(sizeValue)
    );
    return size ? size.to_show : '';
  };

  // Find contract type display name
  const getContractTypeDisplay = (contractValue: number | number[]) => {
    if (Array.isArray(contractValue)) {
      return contractValue
        .map((val) => {
          const contract = contractTypes.find((c) => c.value === val);
          return contract ? contract.to_show : '';
        })
        .filter(Boolean)
        .join(', ');
    }
    const contract = contractTypes.find((c) => c.value === contractValue);
    return contract ? contract.to_show : '';
  };

  // Check if job data is loaded
  if (!job) {
    return (
      <RootStyle>
        <Navbar />
        <WrapperContent>
          <Container
            maxWidth="lg"
            style={{ paddingTop: '50px', paddingBottom: '50px' }}
          >
            <Typography variant="h5" align="center">
              {t('Loading job details...')}
            </Typography>
          </Container>
        </WrapperContent>
        <Footer />
      </RootStyle>
    );
  }

  return (
    <RootStyle>
      <Navbar />
      <WrapperContent>
        <Container
          maxWidth="lg"
          style={{ paddingTop: '50px', paddingBottom: '50px' }}
        >
          <Box
            sx={{
              mb: 3,
              cursor: 'pointer'
            }}
            onClick={() => navigate(-1)}
            className="skillio-back-button"
          >
            <Image name="back.svg" width={16} height={16} />
            <Typography variant="body1" style={{ color: colors.tenantColor }}>
              {t('Back')}
            </Typography>
          </Box>

          <GridStyle container columnGap="38px" wrap="nowrap">
            <Grid item xs={12} md={9} lg={8}>
              <JobCard style={{ cursor: 'default' }}>
                <Grid
                  item
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start'
                  }}
                >
                  <JobDetailHeader>
                    <JobDetailHeaderContent>
                      <JobTitle variant="h5" style={{ marginBottom: 0 }}>
                        {job?.title}
                      </JobTitle>
                      {job?.company?.name && (
                        <JobDetailHeaderLocation>
                          <JobDetailHeaderLocationIcon>
                            <Image
                              className="location"
                              name="portfolio.svg"
                              width={14}
                              height={14}
                            />
                          </JobDetailHeaderLocationIcon>
                          <JobDetailHeaderLocationText variant="body2">
                            {job.company.name}
                          </JobDetailHeaderLocationText>
                        </JobDetailHeaderLocation>
                      )}
                      {jobLocations.length > 0 && (
                        <JobDetailHeaderLocation>
                          <JobDetailHeaderLocationIcon>
                            <Image
                              className="location"
                              name="location.svg"
                              width={12}
                              height={12}
                            />
                          </JobDetailHeaderLocationIcon>
                          <JobDetailHeaderLocationText variant="body2">
                            {jobLocations.join(', ')}
                          </JobDetailHeaderLocationText>
                        </JobDetailHeaderLocation>
                      )}
                    </JobDetailHeaderContent>
                    <JobDetailHeaderLogo
                      onClick={
                        isCompanyPublic ? handleCompanyClick : undefined
                      }
                      sx={{
                        cursor: isCompanyPublic ? 'pointer' : 'default'
                      }}
                    >
                      <Image
                        name={
                          job?.company?.logo ||
                          '/static/images/recrout-logo-letter.jpg'
                        }
                        url={
                          job?.company?.logo ||
                          '/static/images/recrout-logo-letter.jpg'
                        }
                        style={{
                          width: 'auto',
                          maxWidth: '100px',
                          height: 'auto',
                          maxHeight: '80px',
                          objectFit: 'contain'
                        }}
                      />
                    </JobDetailHeaderLogo>
                  </JobDetailHeader>

                  <JobListDescription>
                    <HtmlContent
                      content={job?.description || ''}
                      className="jobdescription__publicjobs"
                    />
                  </JobListDescription>

                  <JobOtherInfo>
                    <CompetencyTitle style={{ marginTop: 0 }}>
                      {t('We are looking for these competencies')}:
                    </CompetencyTitle>
                    {job?.competencies && job?.competencies.length > 0 && (
                      <CompetencyContainer>
                        {job.competencies.map((comp: any, index: number) => (
                          <CompetencyTag
                            key={index}
                            tenantColor={colors.tenantColor}
                            lighterPurple={colors.lightPurple}
                          >
                            {comp.competency.name}
                          </CompetencyTag>
                        ))}
                      </CompetencyContainer>
                    )}
                  </JobOtherInfo>
                </Grid>
              </JobCard>
            </Grid>
            <Grid item xs={12} md={3} lg={4}>
              <Box
                sx={{
                  background: '#fff',
                  borderRadius: '25px',
                  padding: '30px',
                  boxShadow: '0px 0px 12px rgba(0, 0, 0, 0.08)',
                  color: '#131417'
                }}
              >
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  {t('Job Information')}
                </Typography>

                {jobIndustries.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {t('Industry')}
                    </Typography>
                    <Typography variant="body2">
                      {jobIndustries.join(', ')}
                    </Typography>
                  </Box>
                )}

                {job?.degree && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {t('Required Degree')}
                    </Typography>
                    <Typography variant="body2">
                      {DegreeEnum[job.degree]}
                    </Typography>
                  </Box>
                )}

                {job?.work_exp && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {t('Career Level')}
                    </Typography>
                    <Typography variant="body2">
                      {getCareerLevelByValue(job.work_exp)}
                    </Typography>
                  </Box>
                )}

                {(job?.contract || job?.contract_type) && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {t('Contract Type')}
                    </Typography>
                    <Typography variant="body2">
                      {getContractTypeDisplay(
                        job.contract ?? job.contract_type
                      )}
                    </Typography>
                  </Box>
                )}

                {(job?.compensation || job?.salary) && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {t('Compensations (EUR)')}
                    </Typography>
                    <Typography variant="body2">
                      {Array.isArray(job.compensation ?? job.salary)
                        ? (job.compensation ?? job.salary)
                            .map((sal: number) => getSalaryByValue(sal))
                            .filter(Boolean)
                            .join(', ')
                        : getSalaryByValue(job.compensation ?? job.salary)}
                    </Typography>
                  </Box>
                )}

                {/* Company size - added as requested */}
                {job?.company?.company_size && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {t('Company Size')}
                    </Typography>
                    <Typography variant="body2">
                      {getCompanySizeDisplay(job.company.company_size)}
                    </Typography>
                  </Box>
                )}

                {job?.is_remote !== undefined && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {t('Remote Work')}
                    </Typography>
                    <Typography variant="body2">
                      {job.is_remote ? t('Yes') : t('No')}
                    </Typography>
                  </Box>
                )}

                {jobSkills.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {t('Skills')}
                    </Typography>
                    <Typography variant="body2">
                      {jobSkills.join(', ')}
                    </Typography>
                  </Box>
                )}

                {job?.musts && job.musts.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {t('Must Haves')}
                    </Typography>
                    <Typography variant="body2">
                      {job.musts.join(', ')}
                    </Typography>
                  </Box>
                )}

                {/* Apply button with conditional rendering based on user role */}
                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                  {!isAuthenticated ? (
                    <Button
                      onClick={handleNavigateToLogin}
                      t={t}
                      className="navbar__left-btn"
                      title={t('Login or Sign up to apply')}
                    />
                  ) : isCandidate && !disableActions ? (
                    <Button
                      onClick={() => handleApply(job?.id)}
                      t={t}
                      className="navbar__left-btn"
                      disabled={job?.is_applied}
                      title={job?.is_applied ? t('Applied') : t('Apply Now')}
                    />
                  ) : isCompanyUser ? (
                    // No apply button for company users
                    <Typography variant="body2" color="textSecondary">
                      {t('Logged in as a company user')}
                    </Typography>
                  ) : (
                    !disableActions && (
                      <Button
                        onClick={() => handleApply(job?.id)}
                        t={t}
                        className="navbar__left-btn"
                        disabled={job?.is_applied}
                        title={job?.is_applied ? t('Applied') : t('Apply Now')}
                      />
                    )
                  )}
                </Box>
              </Box>
            </Grid>
          </GridStyle>
        </Container>
      </WrapperContent>
      <Footer />
    </RootStyle>
  );
};

export default PublicJobDetail;
